import Joi from 'joi';
import { Form, useNavigate, redirect, useActionData, json, useLoaderData } from "react-router-dom";
import { fetchClient } from '../../util/fetchClient.js';
import { ROLES } from '../_Shared/navigation.js';
import RedExclamationCircle from '../_Shared/RedExclamationCircle.js';

const fetchUser = async id => {
  if (id) {
    const { body: user } = await fetchClient(`/v1/users/${id}`);
    return { user };
  } else {
    return { user: {} };
  }
};

export const loader = async ({ params }) => fetchUser(params && params.id);

const translateRole = role => {
  switch (role) {
    case ROLES.ADMIN:
      return 'Administrador';
    case ROLES.RECEPCIONIST:
      return 'Recepcionista';
    default:
      return role;
  }
};

const getRequestBody = (data) => ({
  first_name: data.first_name,
  last_name: data.last_name,
  phone: data.phone,
  email: data.email,
  role: data.role,
  password: data.password,
  ...(data.id ? { id: data.id } : undefined)
});

const updateUserSchema = Joi.object({
  id: Joi.string().required(),
  first_name: Joi.string().required(),
  last_name: Joi.string().required(),
  phone: Joi.string().optional().allow(null, ''),
  email: Joi.string().email({ tlds: { allow: false } }).required(),
  role: Joi.string().valid(...Object.values(ROLES)).required(),
  password: Joi.string().optional().allow(''),
}).options({ abortEarly: false });

const createUserSchema = Joi.object({
  first_name: Joi.string().required(),
  last_name: Joi.string().required(),
  phone: Joi.string().optional().allow(null, ''),
  email: Joi.string().email({ tlds: { allow: false } }).required(),
  role: Joi.string().valid(...Object.values(ROLES)).required(),
  password: Joi.string().optional().allow(''),
}).options({ abortEarly: false });

export const action = async ({ request }) => {
  const data = Object.fromEntries(await request.formData());
  const body = getRequestBody(data);

  try {
    const userSchema = request.method === 'POST' ? createUserSchema : updateUserSchema;
    await userSchema.validateAsync(body, { abortEarly: false });
  } catch (validationError) {
    console.log(`validationError`, validationError);
    const errors = {};
    validationError?.details.map((details) => errors[details.context.key] = details.message);
    return json({ errors });
  }

  let response;
  switch (request.method) {
    case 'POST':
      response = await fetchClient('/v1/users', { method: 'POST', body: JSON.stringify(body) });
      break;
    case 'PUT':
      response = await fetchClient(`/v1/users/${body.id}`, { method: 'PUT', body: JSON.stringify(body) });
      break;
    default:
  }

  if (response?.response?.ok) {
    return redirect('/users');
  } else {
    const message = `ERROR: Server failed to process the request(${response?.status})`;
    console.log(message);
    return json({ errors: { api: message } });
  }
};

export default function UsersForm() {
  const navigate = useNavigate();
  const cancelButtonClick = () => navigate('/users');
  const actionData = useActionData();
  const { user } = useLoaderData();

  return (
    <Form method={user.id ? "put" : "post"}>
      <input hidden id="id" name="id" defaultValue={user.id} />

      <div className="px-4 sm:px-6 lg:px-8">
        <div className="space-y-12">
          <div className="border-b border-gray-900/10 pb-12">
            <h2 className="text-base font-semibold leading-7 text-gray-900">Información del Usuario</h2>
            <p className="mt-1 text-sm leading-6 text-gray-600">Pide a los nuevos usuarios que cambien su contraseña.</p>

            <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-6">
              <div className='col-span-1'>
                <label htmlFor="country" className="block text-sm font-medium leading-6 text-gray-900">
                  Rol
                </label>
                <div className="mt-2">
                  <select
                    id="role"
                    name="role"
                    autoComplete="role-name"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                    defaultValue={user.role || ROLES.RECEPCIONIST}
                  >
                    {Object.keys(ROLES).map(role => {
                      return <option value={ROLES[role]}>{translateRole(ROLES[role])}</option>
                    })}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium leading-6 text-gray-900">
                  Teléfono
                </label>
                <div className="mt-2">
                  <input
                    id="phone"
                    name="phone"
                    type="text"
                    autoComplete="phone"
                    className={actionData?.errors?.phone
                      ? "block w-full rounded-md border-0 py-1.5 text-rose-900 shadow-sm ring-1 ring-inset ring-rose-300 placeholder:text-rose-400 focus:ring-2 focus:ring-inset focus:ring-rose-600 sm:text-sm sm:leading-6"
                      : "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    }
                    defaultValue={user.phone}
                  />
                </div>
              </div>

            </div>

            <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-6">
              <div>
                <label htmlFor="first-name" className="block text-sm font-medium leading-6 text-gray-900">
                  Nombre(s)
                </label>
                <div className="mt-2">
                  <input
                    id="first_name"
                    name="first_name"
                    type="text"
                    autoComplete="given-name"
                    className={actionData?.errors?.first_name
                      ? "block w-full rounded-md border-0 py-1.5 text-rose-900 shadow-sm ring-1 ring-inset ring-rose-300 placeholder:text-rose-400 focus:ring-2 focus:ring-inset focus:ring-rose-600 sm:text-sm sm:leading-6"
                      : "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    }
                    defaultValue={user.first_name}
                  />
                </div>
              </div>

              <div >
                <label htmlFor="last-name" className="block text-sm font-medium leading-6 text-gray-900">
                  Apellido(s)
                </label>
                <div className="mt-2">
                  <input
                    id="last_name"
                    name="last_name"
                    type="text"
                    autoComplete="family-name"
                    className={actionData?.errors?.last_name
                      ? "block w-full rounded-md border-0 py-1.5 text-rose-900 shadow-sm ring-1 ring-inset ring-rose-300 placeholder:text-rose-400 focus:ring-2 focus:ring-inset focus:ring-rose-600 sm:text-sm sm:leading-6"
                      : "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    }
                    defaultValue={user.last_name}
                  />
                </div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">
                  Email
                </label>
                <div className="mt-2">
                  <input
                    id="email"
                    name="email"
                    type="text"
                    autoComplete="email"
                    className={actionData?.errors?.email
                      ? "block w-full rounded-md border-0 py-1.5 text-rose-900 shadow-sm ring-1 ring-inset ring-rose-300 placeholder:text-rose-400 focus:ring-2 focus:ring-inset focus:ring-rose-600 sm:text-sm sm:leading-6"
                      : "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    }
                    defaultValue={user.email}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium leading-6 text-gray-900">
                  Password
                </label>
                <div className="mt-2">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-x-6">
            <button type="button" onClick={cancelButtonClick} className="text-sm font-semibold leading-6 text-gray-900">
              Cancelar
            </button>
            <button
              type="submit"
              className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              Guardar
            </button>
            {actionData?.errors && actionData.errors ? <RedExclamationCircle /> : null}
          </div>
        </div>
      </div>
    </Form>
  )
}
